import { handleResponseErrors } from '@restDataAccess/handleResponseErrors';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import type { ApiNameType } from '@/constants';

import { authorizationHeader } from '../authorizationHeader.ts';
import { useClearCacheCallback } from './cacheHooks.ts';
import { assertApiBaseForFetch } from './getApiUrl/getApiUrl.functions.ts';
import { useGetApiUrl } from './useGetApiUrl.ts';

type AuthApiDelete = {
  baseApiRoute: ApiNameType;
  cacheKeys: string[];
  headers?: HeadersInit;
  id?: string;
  identifier?: string;
  route: string;
};

export const useAuthApiDelete = <T, V = void>({
  baseApiRoute,
  cacheKeys,
  headers,
  id,
  identifier,
  route,
}: AuthApiDelete) => {
  const auth = useAuth();
  const api = useGetApiUrl(baseApiRoute);
  const clearCacheCallback = useClearCacheCallback(cacheKeys);

  const routeIdSegment = id ? `/${id}` : '';

  const deleteDevice = async (device: Partial<T>) => {
    try {
      const base = assertApiBaseForFetch(api);
      const response = await fetch(`${base}${route}${routeIdSegment}`, {
        method: 'DELETE',
        body: JSON.stringify(device),
        headers: {
          ...authorizationHeader(auth.user),
          'Content-Type': 'application/json',
          ...headers,
        },
      });

      return await handleResponseErrors<V>(response, `${identifier}, ${id ?? ''}`);
    } catch (error) {
      console.error(`Error removing device ${identifier}`, error);
      throw error;
    }
  };

  return useMutation({
    mutationFn: deleteDevice,
    onSuccess: () => clearCacheCallback(),
    onError: () => clearCacheCallback(),
  });
};
